import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, userStore, deviceStatusStore } from "./api";
import { getLoginDeviceFields, setDeviceId, getDeviceId } from "./device";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => userStore.get());
  const [loading, setLoading] = useState(true);
  const [deviceStatus, setDeviceStatus] = useState(() => deviceStatusStore.get());

  const refreshMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      userStore.set(data);
    } catch {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Poll this device's own approval status for field supervisors. Writes
  // only ever surface a 423 "device_not_approved" (i.e. still NOT
  // approved) — a successful admin approval has no way to reach an
  // already-open session otherwise, so the "Device Not Approved" banner
  // would otherwise keep showing forever until the supervisor logs out
  // and back in. This picks up the change (in either direction — approved
  // or revoked) within one polling interval.
  useEffect(() => {
    const isField = user && (user.role === "pmc_supervisor" || user.role === "contractor_supervisor" || user.role === "supervisor_incharge");
    if (!isField) return;
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get("/me/devices");
        if (!alive) return;
        const mine = (data || []).find((d) => d.device_uuid === getDeviceId());
        if (!mine) return;
        const prevStatus = deviceStatusStore.get();
        if (mine.status !== prevStatus) {
          deviceStatusStore.set(mine.status);
          setDeviceStatus(mine.status);
          if (prevStatus === "pending" && mine.status === "approved") {
            toast.success("Device Approved!", {
              description: "Your device approval request has been approved by admin.",
              duration: 8000,
            });
          } else if (mine.status === "revoked") {
            toast.error("Device Access Revoked", {
              description: "Your device access has been revoked by admin.",
              duration: 8000,
            });
          }
        }
      } catch {
        // ignore — will retry on the next tick
      }
    };
    check();
    const id = setInterval(check, 20_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user]);

  const login = async (username, password) => {
    // Attach the device-binding fields (UUID + FingerprintJS hash) so
    // the backend can recognise this physical device on first login and
    // every login thereafter. We `await` the fingerprint specifically so
    // the very first login doesn't get classified as "no fingerprint" —
    // subsequent calls hit the cache and are instant.
    const deviceFields = await getLoginDeviceFields();
    const { data } = await api.post("/auth/login", { username, password, ...deviceFields });
    tokenStore.set(data.access_token);
    userStore.set(data.user);
    setUser(data.user);
    // Persist the device-binding verdict. If the backend rebound an
    // existing device to a new UUID (silent rebind case), trust its
    // value and update our local UUID so we stay in sync.
    if (data.device_id) setDeviceId(data.device_id);
    const ds = data.device_status || "not_required";
    deviceStatusStore.set(ds);
    setDeviceStatus(ds);
    return data.user;
  };

  const logout = () => {
    tokenStore.clear();
    deviceStatusStore.clear();
    setDeviceStatus(null);
    setUser(null);
    try { sessionStorage.removeItem("tnpm_settings_unlocked"); } catch {}
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refreshMe, deviceStatus, setDeviceStatus }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function defaultPathFor(role, user) {
  if (role === "admin") return "/admin/dashboard";
  if (role === "pmc_supervisor") return "/pmc/today";
  if (role === "contractor_supervisor" || role === "supervisor_incharge") return "/contractor/attendance";
  if (role === "contractor_admin") return "/contractor-admin/dashboard";
  return "/login";
}
